import React, { useState, useEffect } from 'react';
import { X, Server, HardDrive, Database, Activity, Cpu, Shield, CheckCircle, Info, Network, Loader2, TrendingUp, Zap, AlertTriangle, Leaf, Flame, Thermometer, WifiOff } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCustomerApi } from '@/contexts/DashboardCustomerContext';
import { formatCapacity } from '@/lib/utils/format';

interface ClusterDetailsModalProps {
  clusterId: string;
  clusterName: string;
  onClose: () => void;
  data?: any;
}

const ClusterDetailsModal: React.FC<ClusterDetailsModalProps> = ({ clusterId, clusterName, onClose, data }) => {
  const apiClient = useCustomerApi();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [clusterDetails, setClusterDetails] = useState<any>({});
  const [risks, setRisks] = useState<any[]>([]);
  const [energyData, setEnergyData] = useState<any>(null);
  const [energyLoading, setEnergyLoading] = useState(false);
  const [autoSupportData, setAutoSupportData] = useState<any>(null);

  useEffect(() => {
    const fetchClusterDetails = async () => {
      setLoading(true);
      try {
        // Fetch cluster-specific data from the backend
        const [systemsRes, aggregatesRes, clustersRes, risksRes, autoSupportRes] = await Promise.all([
          apiClient.get('/netapp/systems').catch(() => ({ data: [] })),
          apiClient.get('/netapp/aggregates').catch(() => ({ data: [] })),
          apiClient.get('/netapp/clusters').catch(() => ({ data: [] })),
          apiClient.get('/netapp/risks').catch(() => ({ data: [] })),
          apiClient.get('/dashboard/autosupport').catch(() => ({ data: { systems: [] } }))
        ]);

        setClusterDetails({
          systems: systemsRes.data || [],
          aggregates: aggregatesRes.data || [],
          clusters: clustersRes.data || []
        });

        // Store AutoSupport data
        setAutoSupportData(autoSupportRes.data);

        // Filter risks for this cluster
        const clusterSystems = systemsRes.data?.filter((s: any) =>
          s.cluster_name === clusterName || s.cluster_id === clusterId
        ) || [];

        const systemIds = clusterSystems.map((s: any) => s.system_id);

        const clusterRisks = risksRes.data?.filter((risk: any) =>
          risk.instances?.some((instance: any) =>
            systemIds.includes(instance.system_id) ||
            instance.cluster_id === clusterId ||
            instance.cluster_name === clusterName
          )
        ).map((risk: any) => ({
          ...risk,
          // Filter instances to only show those from this cluster
          instances: risk.instances?.filter((instance: any) =>
            systemIds.includes(instance.system_id) ||
            instance.cluster_id === clusterId ||
            instance.cluster_name === clusterName
          ) || []
        })) || [];

        setRisks(clusterRisks);
      } catch (error) {
        console.error('Error fetching cluster details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchClusterDetails();
  }, [clusterId, clusterName]);

  // Extract cluster data from fetched data
  const clusterData = clusterDetails?.clusters?.find((c: any) =>
    c.cluster_id === clusterId || c.cluster_name === clusterName
  ) || data?.ha_pairs?.find((p: any) => p.cluster_id === clusterId || p.cluster_name === clusterName) || {};

  const aggregates = clusterDetails?.aggregates?.filter((a: any) =>
    a.cluster_id === clusterId || a.cluster_name === clusterName
  ) || [];

  const systems = clusterDetails?.systems?.filter((s: any) =>
    s.cluster_name === clusterName || s.cluster_id === clusterId
  ) || [];

  // Helper function to get AutoSupport status for a system
  const getAutoSupportStatus = (systemId: string) => {
    if (!autoSupportData || !autoSupportData.systems) return null;
    return autoSupportData.systems.find((asup: any) => asup.system_id === systemId);
  };

  // Fetch energy data when energy tab is selected
  useEffect(() => {
    if (activeTab === 'energy' && !energyData) {
      const fetchEnergyData = async () => {
        setEnergyLoading(true);
        try {
          const response = await apiClient.get('/dashboard/energy');
          setEnergyData(response.data);
        } catch (error) {
          console.error('Error fetching energy data:', error);
        } finally {
          setEnergyLoading(false);
        }
      };
      fetchEnergyData();
    }
  }, [activeTab, energyData]);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Info },
    { id: 'storage', label: 'Storage', icon: Database },
    { id: 'performance', label: 'Performance', icon: Activity },
    { id: 'hardware', label: 'Hardware', icon: Cpu },
    { id: 'network', label: 'Network', icon: Network },
    { id: 'energy', label: 'Energy', icon: Zap },
    { id: 'risks', label: 'Risks', icon: AlertTriangle },
  ];

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white dark:bg-slate-800/95 rounded-xl shadow-2xl max-w-6xl w-full max-h-[85vh] overflow-hidden"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Server className="w-6 h-6 text-blue-500" />
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {clusterName}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Cluster ID: {clusterId}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex gap-4">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                    activeTab === tab.id
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(85vh - 180px)' }}>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              </div>
            ) : activeTab === 'overview' ? (
              <div className="space-y-6">
                {/* Cluster Status */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Status</div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-lg font-semibold text-green-600 dark:text-green-400">
                        {clusterData.status || 'Active'}
                      </span>
                    </div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Node Count</div>
                    <div className="text-lg font-semibold text-gray-900 dark:text-white">
                      {clusterData.node_count || systems.length || 0} Nodes
                    </div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Location</div>
                    <div className="text-lg font-semibold text-gray-900 dark:text-white">
                      {(() => {
                        // Try to get location from cluster data first
                        if (clusterData.location) {
                          return clusterData.location;
                        }

                        // Otherwise, get location from first system if all systems have same location
                        if (systems.length > 0) {
                          const firstSystem = systems[0];
                          const city = firstSystem.location_city;
                          const country = firstSystem.location_country;

                          if (city && country) {
                            return `${city}, ${country}`;
                          } else if (city) {
                            return city;
                          } else if (firstSystem.location) {
                            return firstSystem.location;
                          }
                        }

                        return 'N/A';
                      })()}
                    </div>
                  </div>
                </div>

                {/* Systems in Cluster */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    Systems in Cluster
                  </h3>
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-100 dark:bg-slate-800/95 border-b border-gray-200 dark:border-gray-700">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                              Node / System Name
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                              Model
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                              Serial Number
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                              ONTAP Version
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                              Location
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                              Support Level
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                              AutoSupport Status
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          {systems.map((system: any, idx: number) => {
                            const asupStatus = getAutoSupportStatus(system.system_id);
                            return (
                            <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                              <td className="px-4 py-3">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {system.system_name || system.host_name || 'N/A'}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  {system.platform_type || 'Storage Node'}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="text-sm text-gray-900 dark:text-gray-300">
                                  {system.model || 'N/A'}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="text-sm font-mono text-gray-600 dark:text-gray-400">
                                  {system.serial_number || 'N/A'}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="text-sm text-gray-900 dark:text-gray-300">
                                  {system.os_version || 'N/A'}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="text-sm text-gray-900 dark:text-gray-300">
                                  {(() => {
                                    const city = system.location_city;
                                    const country = system.location_country;
                                    const location = system.location;

                                    if (city && country) {
                                      return `${city}, ${country}`;
                                    } else if (city) {
                                      return city;
                                    } else if (location) {
                                      return location;
                                    } else {
                                      return 'N/A';
                                    }
                                  })()}
                                </div>
                                {system.location_site_id && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    Site ID: {system.location_site_id}
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <div className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                  system.service_tier === 'PREMIUM' || system.service_tier === 'premium'
                                    ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                                    : system.service_tier === 'ENHANCED' || system.service_tier === 'enhanced'
                                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                }`}>
                                  {system.service_tier || 'STANDARD'}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                {asupStatus ? (
                                  <div className="space-y-1">
                                    {/* Status Badge */}
                                    <div className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-full ${
                                      asupStatus.status === 'ON'
                                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                        : asupStatus.status === 'RSS'
                                        ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
                                        : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                    }`}>
                                      {asupStatus.status === 'ON' ? (
                                        <CheckCircle className="w-3 h-3" />
                                      ) : asupStatus.status === 'RSS' ? (
                                        <AlertTriangle className="w-3 h-3" />
                                      ) : (
                                        <WifiOff className="w-3 h-3" />
                                      )}
                                      {asupStatus.status}
                                    </div>

                                    {/* Transport Method */}
                                    <div className="text-xs text-gray-600 dark:text-gray-400">
                                      Transport: <span className="font-medium">{asupStatus.transport || 'Unknown'}</span>
                                    </div>

                                    {/* Generated Date - Shows system is creating ASUPs */}
                                    {asupStatus.days_since_generated !== null && asupStatus.days_since_generated !== undefined && (
                                      <div className="text-xs text-gray-600 dark:text-gray-400">
                                        Generated: <span className={`font-medium ${
                                          asupStatus.days_since_generated > 1 ? 'text-yellow-600 dark:text-yellow-400' : 'text-green-600 dark:text-green-400'
                                        }`}>{asupStatus.days_since_generated}d ago</span>
                                      </div>
                                    )}

                                    {/* Received Date - Critical connectivity indicator */}
                                    {asupStatus.days_since_received !== null && asupStatus.days_since_received !== undefined ? (
                                      <div className="text-xs text-gray-600 dark:text-gray-400">
                                        Received: <span className={`font-medium ${
                                          asupStatus.days_since_received > 3
                                            ? 'text-red-600 dark:text-red-400'
                                            : asupStatus.days_since_received > 1
                                            ? 'text-yellow-600 dark:text-yellow-400'
                                            : 'text-green-600 dark:text-green-400'
                                        }`}>{asupStatus.days_since_received}d ago</span>
                                      </div>
                                    ) : (
                                      /* CRITICAL: NetApp never received the ASUP */
                                      <div className="flex items-center gap-1 px-2 py-1 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-xs">
                                        <AlertTriangle className="w-3 h-3 text-red-600 dark:text-red-400" />
                                        <span className="text-red-700 dark:text-red-300 font-medium">Not Received by NetApp</span>
                                      </div>
                                    )}

                                    {/* OnDemand Status for advanced support features */}
                                    {(asupStatus.is_ondemand_enabled || asupStatus.is_ondemand_capable) && (
                                      <div className="text-xs text-blue-600 dark:text-blue-400">
                                        OnDemand: {asupStatus.is_ondemand_enabled ? 'Enabled' : asupStatus.is_ondemand_capable ? 'Capable' : 'N/A'}
                                      </div>
                                    )}

                                    {/* Last ASUP Details */}
                                    {asupStatus.latest_asup_type && (
                                      <div className="text-xs text-gray-500 dark:text-gray-500">
                                        Type: {asupStatus.latest_asup_type}{asupStatus.latest_asup_is_manual ? ' (Manual)' : ''}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-xs text-gray-400 dark:text-gray-500">No ASUP data</span>
                                )}
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Capacity Overview */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    Capacity Overview
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                      <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Total Capacity</div>
                      <div className="text-2xl font-bold text-gray-900 dark:text-white">
                        {formatCapacity(clusterData.total_capacity_tb ||
                         systems.reduce((sum: number, s: any) => sum + (s.usable_capacity_tb || 0), 0))}
                      </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                      <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Used Capacity</div>
                      <div className="text-2xl font-bold text-gray-900 dark:text-white">
                        {formatCapacity(clusterData.used_capacity_tb ||
                         systems.reduce((sum: number, s: any) => sum + (s.used_capacity_tb || 0), 0))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Storage Efficiency */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    Storage Efficiency
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">Capacity Utilization</span>
                      </div>
                      <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                        {(() => {
                          const total = clusterData.total_capacity_tb || systems.reduce((sum: number, s: any) => sum + (s.usable_capacity_tb || 0), 0);
                          const used = clusterData.used_capacity_tb || systems.reduce((sum: number, s: any) => sum + (s.used_capacity_tb || 0), 0);
                          const utilization = total > 0 ? (used / total * 100) : 0;
                          return `${utilization.toFixed(1)}%`;
                        })()}
                      </div>
                      <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                        {(() => {
                          const total = clusterData.total_capacity_tb || systems.reduce((sum: number, s: any) => sum + (s.usable_capacity_tb || 0), 0);
                          const used = clusterData.used_capacity_tb || systems.reduce((sum: number, s: any) => sum + (s.used_capacity_tb || 0), 0);
                          const available = total - used;
                          return `${formatCapacity(available)} Available`;
                        })()}
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/30 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Zap className="w-4 h-4 text-green-600 dark:text-green-400" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">Efficiency Ratio</span>
                      </div>
                      <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                        {(() => {
                          // Calculate average efficiency ratio from aggregates
                          const validAggregates = aggregates.filter((a: any) => a.efficiency_ratio_without_snapshot > 0);
                          const avgEfficiency = validAggregates.length > 0
                            ? validAggregates.reduce((sum: number, a: any) => sum + (a.efficiency_ratio_without_snapshot || 0), 0) / validAggregates.length
                            : 0;

                          // If we have efficiency data, show it as a ratio (e.g., "2.5:1")
                          if (avgEfficiency > 1) {
                            return `${avgEfficiency.toFixed(1)}:1`;
                          } else if (avgEfficiency > 0) {
                            return avgEfficiency.toFixed(2);
                          }
                          return 'N/A';
                        })()}
                      </div>
                      <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                        {aggregates.some((a: any) => a.efficiency_ratio_without_snapshot > 0)
                          ? 'Data reduction ratio'
                          : 'Average across systems'}
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/30 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Database className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">Space Savings</span>
                      </div>
                      <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                        {(() => {
                          // Calculate space savings from efficiency ratio
                          const validAggregates = aggregates.filter((a: any) => a.efficiency_ratio_without_snapshot > 1);
                          if (validAggregates.length > 0) {
                            const avgEfficiency = validAggregates.reduce((sum: number, a: any) =>
                              sum + (a.efficiency_ratio_without_snapshot || 0), 0) / validAggregates.length;
                            const savingsPercent = ((avgEfficiency - 1) / avgEfficiency * 100);
                            return `${savingsPercent.toFixed(1)}%`;
                          }
                          return 'Coming Soon';
                        })()}
                      </div>
                      <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                        {aggregates.some((a: any) => a.efficiency_ratio_without_snapshot > 1)
                          ? 'From dedup & compression'
                          : 'Dedup & Compression'}
                      </div>
                    </div>
                  </div>

                  {/* Efficiency breakdown */}
                  {(aggregates.length > 0 || systems.length > 0) && (
                    <div className="mt-4 bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                      <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        {aggregates.some((a: any) => a.efficiency_ratio_without_snapshot)
                          ? 'Aggregate Efficiency Details'
                          : 'System Efficiency Details'}
                      </div>

                      {/* Show aggregate efficiency if available */}
                      {aggregates.some((a: any) => a.efficiency_ratio_without_snapshot) ? (
                        <div className="space-y-3">
                          {aggregates.filter((agg: any) => agg.aggregate_name).map((agg: any, idx: number) => (
                            <div key={idx} className="border-b border-gray-200 dark:border-gray-700 pb-2 last:border-0">
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                                    {agg.aggregate_name}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {agg.raid_type} • {agg.storage_type || 'Unknown'}
                                  </div>
                                </div>
                                <div className="text-right">
                                  {agg.efficiency_ratio_without_snapshot > 0 && (
                                    <>
                                      <div className="text-sm font-semibold text-green-600 dark:text-green-400">
                                        {agg.efficiency_ratio_without_snapshot.toFixed(2)}:1
                                      </div>
                                      <div className="text-xs text-gray-500 dark:text-gray-400">
                                        Efficiency
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2 mt-2">
                                <div className="flex-1 text-xs text-gray-600 dark:text-gray-400">
                                  {formatCapacity(agg.used_size_tb || 0)} / {formatCapacity(agg.total_size_tb || 0)} Used
                                </div>
                                {agg.efficiency_ratio_with_snapshot && agg.efficiency_ratio_with_snapshot !== agg.efficiency_ratio_without_snapshot && (
                                  <div className="text-xs text-blue-600 dark:text-blue-400">
                                    With snapshots: {agg.efficiency_ratio_with_snapshot.toFixed(2)}:1
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        /* Fall back to system utilization if no efficiency data */
                        <div className="space-y-2">
                          {systems.map((system: any, idx: number) => (
                            <div key={idx} className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {system.host_name || system.serial_number}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  {formatCapacity(system.used_capacity_tb || 0)} / {formatCapacity(system.usable_capacity_tb || 0)}
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="text-right">
                                  <div className="text-sm font-semibold text-gray-900 dark:text-white">
                                    {system.efficiency_ratio ? (system.efficiency_ratio * 100).toFixed(1) : '0.0'}%
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    Utilization
                                  </div>
                                </div>
                                <div className="w-24 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                  <div
                                    className={`h-2 rounded-full ${
                                      (system.efficiency_ratio || 0) > 0.8 ? 'bg-red-500' :
                                      (system.efficiency_ratio || 0) > 0.6 ? 'bg-yellow-500' : 'bg-green-500'
                                    }`}
                                    style={{ width: `${(system.efficiency_ratio || 0) * 100}%` }}
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : activeTab === 'storage' ? (
              <div className="space-y-6">
                {/* Aggregates */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    Aggregates
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <th className="pb-3">Name</th>
                          <th className="pb-3">Type</th>
                          <th className="pb-3">Size</th>
                          <th className="pb-3">Used</th>
                          <th className="pb-3">Available</th>
                          <th className="pb-3">Usage %</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {aggregates.length > 0 ? aggregates.map((agg: any, idx: number) => (
                          <tr key={idx}>
                            <td className="py-3">
                              <span className="font-medium text-gray-900 dark:text-white">
                                {agg.aggregate_name}
                              </span>
                            </td>
                            <td className="py-3 text-sm text-gray-600 dark:text-gray-400">
                              {agg.raid_type || 'RAID-DP'}
                            </td>
                            <td className="py-3 text-sm text-gray-900 dark:text-white">
                              {formatCapacity(agg.total_size_tb || 0)}
                            </td>
                            <td className="py-3 text-sm text-gray-900 dark:text-white">
                              {formatCapacity(agg.used_size_tb || 0)}
                            </td>
                            <td className="py-3 text-sm text-gray-900 dark:text-white">
                              {formatCapacity(agg.available_size_tb || 0)}
                            </td>
                            <td className="py-3">
                              <div className="flex items-center gap-2">
                                <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2 max-w-[60px]">
                                  <div
                                    className={`h-2 rounded-full ${
                                      agg.percent_used > 80 ? 'bg-red-500' :
                                      agg.percent_used > 60 ? 'bg-yellow-500' : 'bg-green-500'
                                    }`}
                                    style={{ width: `${agg.percent_used || 0}%` }}
                                  />
                                </div>
                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                  {agg.percent_used?.toFixed(1) || 0}%
                                </span>
                              </div>
                            </td>
                          </tr>
                        )) : (
                          <tr>
                            <td colSpan={6} className="py-8 text-center text-gray-500 dark:text-gray-400">
                              No aggregate data available
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Disk Summary */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    Disk Information
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <HardDrive className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">Total Disks</span>
                      </div>
                      <div className="text-xl font-semibold text-gray-900 dark:text-white">
                        {clusterData.disk_count || 'N/A'}
                      </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Database className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">Drive Models</span>
                      </div>
                      <div className="text-xs text-gray-900 dark:text-white space-y-1">
                        {(() => {
                          const diskTypes = clusterData.disk_types;
                          if (!diskTypes || diskTypes === 'N/A') return 'N/A';

                          // Parse model:count pairs
                          const models = diskTypes.split(', ');
                          return models.map((model: string, idx: number) => {
                            const [modelName, count] = model.split(':');
                            if (count) {
                              // Show model name and count
                              return (
                                <div key={idx} className="flex justify-between">
                                  <span className="truncate max-w-[180px]" title={modelName}>{modelName}</span>
                                  <span className="font-semibold ml-2">×{count}</span>
                                </div>
                              );
                            } else {
                              // Fallback to just showing the type if no count
                              return <div key={idx}>{model}</div>;
                            }
                          });
                        })()}
                      </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Shield className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">Protection</span>
                      </div>
                      <div className="text-sm text-gray-900 dark:text-white">
                        RAID-DP
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : activeTab === 'performance' ? (
              <div className="flex flex-col items-center justify-center py-16 space-y-4">
                <div className="bg-blue-100 dark:bg-blue-900/30 rounded-full p-4">
                  <Activity className="w-12 h-12 text-blue-500 dark:text-blue-400" />
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
                  Performance Metrics Coming Soon
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-center max-w-md">
                  Real-time performance monitoring including IOPS, throughput, latency, and CPU usage will be available in a future update.
                </p>
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 max-w-lg">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-blue-500 mt-0.5" />
                    <div className="text-sm text-blue-700 dark:text-blue-300">
                      <p className="font-medium mb-1">What to expect:</p>
                      <ul className="list-disc list-inside space-y-1 text-blue-600 dark:text-blue-400">
                        <li>Real-time IOPS monitoring</li>
                        <li>Throughput analytics</li>
                        <li>Latency tracking</li>
                        <li>CPU and memory usage graphs</li>
                        <li>Historical performance trends</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ) : activeTab === 'hardware' ? (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    Hardware Configuration
                  </h3>
                  <div className="space-y-4">
                    {systems.map((system: any, idx: number) => (
                      <div key={idx} className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                        <div className="font-medium text-gray-900 dark:text-white mb-3">
                          {system.system_name}
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">Model</div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {system.model}
                            </div>
                          </div>
                          <div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">Serial Number</div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {system.serial_number}
                            </div>
                          </div>
                          <div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">OS Version</div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {system.os_version}
                            </div>
                          </div>
                          <div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">Support Status</div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {system.support_status || 'Active'}
                            </div>
                          </div>
                        </div>

                        {/* Capacity Information */}
                        {(system.raw_capacity_tb || system.usable_capacity_tb) && (
                          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Capacity</div>
                            <div className="grid grid-cols-3 gap-4">
                              {system.raw_capacity_tb && (
                                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                                  <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Raw Capacity</div>
                                  <div className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                                    {formatCapacity(system.raw_capacity_tb)}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                    Physical disks
                                  </div>
                                </div>
                              )}
                              {system.usable_capacity_tb && (
                                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                                  <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Usable Capacity</div>
                                  <div className="text-sm font-semibold text-green-600 dark:text-green-400">
                                    {formatCapacity(system.usable_capacity_tb)}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                    After RAID/HA
                                  </div>
                                </div>
                              )}
                              {system.raw_capacity_tb && system.usable_capacity_tb && (
                                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3">
                                  <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Efficiency</div>
                                  <div className="text-sm font-semibold text-purple-600 dark:text-purple-400">
                                    {((system.usable_capacity_tb / system.raw_capacity_tb) * 100).toFixed(1)}%
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                    Usable/Raw ratio
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Shelves */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    Disk Shelves
                  </h3>
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Shelves</div>
                        <div className="text-2xl font-semibold text-gray-900 dark:text-white">
                          {clusterData.shelf_count || 'N/A'}
                        </div>
                      </div>
                      {clusterData.shelf_count && clusterData.disk_count && (
                        <div className="text-right">
                          <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Avg Disks per Shelf</div>
                          <div className="text-lg font-medium text-gray-900 dark:text-white">
                            {Math.round(clusterData.disk_count / clusterData.shelf_count)}
                          </div>
                        </div>
                      )}
                    </div>
                    {clusterData.shelf_count > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Model: DS224-12 (typical for AFF-A300 systems)
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : activeTab === 'network' ? (
              <div className="flex flex-col items-center justify-center py-16 space-y-4">
                <div className="bg-green-100 dark:bg-green-900/30 rounded-full p-4">
                  <Network className="w-12 h-12 text-green-500 dark:text-green-400" />
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
                  Network Configuration Coming Soon
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-center max-w-md">
                  Comprehensive network monitoring and configuration details will be available in a future update.
                </p>
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 max-w-lg">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-green-500 mt-0.5" />
                    <div className="text-sm text-green-700 dark:text-green-300">
                      <p className="font-medium mb-1">What to expect:</p>
                      <ul className="list-disc list-inside space-y-1 text-green-600 dark:text-green-400">
                        <li>Network interface (LIF) management</li>
                        <li>Port configuration and status</li>
                        <li>VLAN and subnet details</li>
                        <li>Network throughput monitoring</li>
                        <li>Failover group configuration</li>
                        <li>IPspace and broadcast domains</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ) : activeTab === 'energy' ? (
              energyLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                </div>
              ) : energyData && energyData.systems ? (
                (() => {
                  // Filter energy data for this cluster
                  const clusterEnergySystem = energyData.systems.find((s: any) =>
                    s.id === clusterId || s.name === clusterName || systems.some((sys: any) =>
                      sys.system_id === s.id || sys.host_name === s.name
                    )
                  );

                  if (!clusterEnergySystem) {
                    return (
                      <div className="flex flex-col items-center justify-center py-16 space-y-4">
                        <div className="bg-yellow-100 dark:bg-yellow-900/30 rounded-full p-4">
                          <Zap className="w-12 h-12 text-yellow-500 dark:text-yellow-400" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                          No Energy Data Available
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 text-center max-w-md">
                          Energy metrics for this cluster are not yet available. Data collection may still be in progress.
                        </p>
                      </div>
                    );
                  }

                  const metrics = [
                    {
                      icon: Zap,
                      label: 'Power Consumption',
                      value: clusterEnergySystem.power_w,
                      unit: 'W',
                      color: 'text-yellow-600 dark:text-yellow-400',
                      bgColor: 'bg-yellow-50 dark:bg-yellow-900/20'
                    },
                    {
                      icon: Flame,
                      label: 'Heat Output',
                      value: clusterEnergySystem.heat_btu,
                      unit: 'BTU',
                      color: 'text-orange-600 dark:text-orange-400',
                      bgColor: 'bg-orange-50 dark:bg-orange-900/20'
                    },
                    {
                      icon: Leaf,
                      label: 'Carbon Footprint',
                      value: clusterEnergySystem.carbon_kg,
                      unit: 'Kg CO₂',
                      color: 'text-green-600 dark:text-green-400',
                      bgColor: 'bg-green-50 dark:bg-green-900/20'
                    },
                    {
                      icon: Thermometer,
                      label: 'Ambient Temperature',
                      value: clusterEnergySystem.ambient_temp_c,
                      unit: '°C',
                      color: 'text-blue-600 dark:text-blue-400',
                      bgColor: 'bg-blue-50 dark:bg-blue-900/20'
                    }
                  ];

                  return (
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                          Energy & Environmental Metrics
                        </h3>

                        {/* Main Metrics Grid */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                          {metrics.map((metric, idx) => {
                            const Icon = metric.icon;
                            return (
                              <div
                                key={idx}
                                className="flex flex-col items-center p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow"
                              >
                                <div className={`p-2 rounded-lg ${metric.bgColor} mb-2`}>
                                  <Icon className={`w-5 h-5 ${metric.color}`} />
                                </div>
                                <div className="text-center">
                                  <div className="text-2xl font-semibold text-gray-900 dark:text-white">
                                    {metric.value !== null && metric.value !== undefined
                                      ? metric.value.toLocaleString(undefined, { maximumFractionDigits: 2 })
                                      : 'N/A'}
                                  </div>
                                  <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                    {metric.unit}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                    {metric.label}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Efficiency Metrics */}
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                          Efficiency Metrics
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-900/50">
                            <TrendingUp className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                            <div className="flex-1">
                              <div className="flex items-baseline gap-2">
                                <span className="text-lg font-semibold text-gray-900 dark:text-white">
                                  {clusterEnergySystem.power_efficiency_wtb !== null && clusterEnergySystem.power_efficiency_wtb !== undefined
                                    ? clusterEnergySystem.power_efficiency_wtb.toFixed(2)
                                    : 'N/A'}
                                </span>
                                <span className="text-xs text-gray-600 dark:text-gray-400">
                                  W/TB
                                </span>
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-500">
                                Average watts per terabyte
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-900/50">
                            <Leaf className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                            <div className="flex-1">
                              <div className="flex items-baseline gap-2">
                                <span className="text-lg font-semibold text-gray-900 dark:text-white">
                                  {clusterEnergySystem.carbon_efficiency_kgtb !== null && clusterEnergySystem.carbon_efficiency_kgtb !== undefined
                                    ? clusterEnergySystem.carbon_efficiency_kgtb.toFixed(4)
                                    : 'N/A'}
                                </span>
                                <span className="text-xs text-gray-600 dark:text-gray-400">
                                  Kg/TB
                                </span>
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-500">
                                Average carbon per terabyte
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Data Collection Info */}
                      {clusterEnergySystem.generated_date && (
                        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                          <div className="flex items-center gap-2 text-sm text-blue-700 dark:text-blue-300">
                            <Info className="w-4 h-4" />
                            <span>
                              Last updated: {new Date(clusterEnergySystem.generated_date).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()
              ) : (
                <div className="flex flex-col items-center justify-center py-16 space-y-4">
                  <div className="bg-gray-100 dark:bg-gray-900/30 rounded-full p-4">
                    <Zap className="w-12 h-12 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                    No Energy Data Available
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-center max-w-md">
                    Energy metrics are not yet available. Please check back later.
                  </p>
                </div>
              )
            ) : activeTab === 'risks' ? (
              <div className="space-y-6">
                {/* Risk Summary */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    Risk Summary
                  </h3>
                  <div className="grid grid-cols-5 gap-3">
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                        <span className="text-sm font-medium text-red-700 dark:text-red-400">Critical</span>
                      </div>
                      <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                        {risks.filter(r => r.severity === 'CRITICAL' || r.severity === 'Critical' || r.severity === 'critical').length}
                      </div>
                    </div>
                    <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-2 h-2 bg-orange-500 rounded-full" />
                        <span className="text-sm font-medium text-orange-700 dark:text-orange-400">High</span>
                      </div>
                      <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                        {risks.filter(r => r.severity === 'HIGH' || r.severity === 'High' || r.severity === 'high').length}
                      </div>
                    </div>
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-2 h-2 bg-yellow-500 rounded-full" />
                        <span className="text-sm font-medium text-yellow-700 dark:text-yellow-400">Medium</span>
                      </div>
                      <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                        {risks.filter(r => r.severity === 'MEDIUM' || r.severity === 'Medium' || r.severity === 'medium').length}
                      </div>
                    </div>
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full" />
                        <span className="text-sm font-medium text-blue-700 dark:text-blue-400">Low</span>
                      </div>
                      <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                        {risks.filter(r => r.severity === 'LOW' || r.severity === 'Low' || r.severity === 'low').length}
                      </div>
                    </div>
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full" />
                        <span className="text-sm font-medium text-green-700 dark:text-green-400">Best Practice</span>
                      </div>
                      <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                        {risks.filter(r => r.severity === 'BEST_PRACTICE').length}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Risk List */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    Active Risks
                  </h3>
                  {risks.length > 0 ? (
                    <div className="space-y-4">
                      {risks
                        .sort((a, b) => {
                          // Sort by severity (Critical > High > Medium > Low/Best Practice)
                          const severityOrder: Record<string, number> = {
                            'CRITICAL': 0, 'critical': 0, 'Critical': 0,
                            'HIGH': 1, 'high': 1, 'High': 1,
                            'MEDIUM': 2, 'medium': 2, 'Medium': 2,
                            'LOW': 3, 'low': 3, 'Low': 3,
                            'BEST_PRACTICE': 4
                          };
                          return (severityOrder[a.severity] || 5) - (severityOrder[b.severity] || 5);
                        })
                        .map((risk: any, idx: number) => (
                          <div
                            key={idx}
                            className={`border rounded-lg p-4 ${
                              risk.severity === 'CRITICAL' || risk.severity === 'Critical' || risk.severity === 'critical'
                                ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/10'
                                : risk.severity === 'HIGH' || risk.severity === 'High' || risk.severity === 'high'
                                ? 'border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/10'
                                : risk.severity === 'MEDIUM' || risk.severity === 'Medium' || risk.severity === 'medium'
                                ? 'border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-900/10'
                                : risk.severity === 'BEST_PRACTICE'
                                ? 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/10'
                                : 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/10'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <AlertTriangle
                                    className={`w-5 h-5 ${
                                      risk.severity === 'CRITICAL' || risk.severity === 'Critical' || risk.severity === 'critical'
                                        ? 'text-red-600 dark:text-red-400'
                                        : risk.severity === 'HIGH' || risk.severity === 'High' || risk.severity === 'high'
                                        ? 'text-orange-600 dark:text-orange-400'
                                        : risk.severity === 'MEDIUM' || risk.severity === 'Medium' || risk.severity === 'medium'
                                        ? 'text-yellow-600 dark:text-yellow-400'
                                        : risk.severity === 'BEST_PRACTICE'
                                        ? 'text-green-600 dark:text-green-400'
                                        : 'text-blue-600 dark:text-blue-400'
                                    }`}
                                  />
                                  <h4 className="font-medium text-gray-900 dark:text-white">
                                    {risk.category || 'General Risk'}
                                  </h4>
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    Risk ID: {risk.risk_id}
                                  </span>
                                  <span
                                    className={`px-2 py-0.5 rounded text-xs font-medium ${
                                      risk.severity === 'CRITICAL' || risk.severity === 'Critical' || risk.severity === 'critical'
                                        ? 'bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200'
                                        : risk.severity === 'HIGH' || risk.severity === 'High' || risk.severity === 'high'
                                        ? 'bg-orange-200 dark:bg-orange-800 text-orange-800 dark:text-orange-200'
                                        : risk.severity === 'MEDIUM' || risk.severity === 'Medium' || risk.severity === 'medium'
                                        ? 'bg-yellow-200 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200'
                                        : risk.severity === 'BEST_PRACTICE'
                                        ? 'bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200'
                                        : 'bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200'
                                    }`}
                                  >
                                    {risk.severity === 'BEST_PRACTICE' ? 'Best Practice' : risk.severity}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                                  {risk.message || risk.description || 'Risk detected'}
                                </p>
                                {risk.correctiveAction && (
                                  <div className="mt-3 p-3 bg-white dark:bg-slate-800/95 rounded-lg">
                                    <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                      Recommended Action:
                                    </div>
                                    <p className="text-sm text-gray-800 dark:text-gray-200">
                                      {risk.correctiveAction}
                                    </p>
                                  </div>
                                )}
                                {risk.instances && risk.instances.length > 0 && (
                                  <div className="mt-3 p-2 bg-gray-100 dark:bg-gray-900 rounded">
                                    <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                                      Affected Systems ({risk.instances.length}):
                                    </div>
                                    <div className="grid grid-cols-2 gap-1 text-xs">
                                      {risk.instances.slice(0, 6).map((instance: any, idx: number) => (
                                        <div key={idx} className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                                          <span className="font-mono">{instance.host_name || instance.system_name || 'System'}</span>
                                          <span className="text-gray-500 dark:text-gray-500">Serial: {instance.serial_number || instance.system_id}</span>
                                        </div>
                                      ))}
                                      {risk.instances.length > 6 && (
                                        <div className="col-span-2 text-gray-500 dark:text-gray-400 italic mt-1">
                                          ... and {risk.instances.length - 6} more systems
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6">
                      <div className="flex items-center justify-center space-x-3">
                        <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
                        <div>
                          <h4 className="text-lg font-medium text-green-800 dark:text-green-200">
                            No Active Risks
                          </h4>
                          <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                            This cluster is operating without any identified risks
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Risk Categories Breakdown */}
                {risks.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      Risk Categories
                    </h3>
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                      <div className="space-y-3">
                        {Object.entries(
                          risks.reduce((acc: any, risk) => {
                            const category = risk.category || 'Uncategorized';
                            if (!acc[category]) {
                              acc[category] = { count: 0, severities: {} };
                            }
                            acc[category].count++;
                            const severity = risk.severity || 'Unknown';
                            acc[category].severities[severity] = (acc[category].severities[severity] || 0) + 1;
                            return acc;
                          }, {})
                        ).map(([category, data]: [string, any]) => (
                          <div key={category} className="flex items-center justify-between">
                            <div>
                              <div className="font-medium text-gray-900 dark:text-white">
                                {category}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {Object.entries(data.severities)
                                  .map(([sev, count]) => `${count} ${sev}`)
                                  .join(', ')}
                              </div>
                            </div>
                            <div className="text-lg font-semibold text-gray-900 dark:text-white">
                              {data.count}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ClusterDetailsModal;